// engine-exception: portal people — a client administrator's colleague management (invite / disable / role), the customer's own profile + notification preferences, and their notification list; self-service over the portal principal, not staff resource screens
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../engine/api/context';
import { Icon } from '../../engine/design/icons';
import { Button } from '../../engine/design/primitives/Button';
import { Input, Switch } from '../../engine/design/primitives/Input';
import { Avatar, Badge, Banner, EmptyState, PageHeader } from '../../engine/design/primitives/Misc';
import { Dialog, DropdownMenu, useConfirm } from '../../engine/design/primitives/Overlay';
import { AutoForm } from '../../engine/forms/AutoForm';
import type { FieldMeta } from '../../engine/meta/types';
import { formatDate, formatDateTime } from '../../lib/format';
import { usePortal } from '../PortalContext';
import { usePortalGet, usePortalList, usePortalMutation } from '../hooks';
import type { NotificationRow, PersonRow } from '../types';
import { ListState, LoadMore } from '../ui';

interface RoleRow { id: string; key: string; name: string; description: string | null; is_default: boolean }

// ── People (client administrators) ───────────────────────────────────────────
export function PeoplePage() {
  const { me, can } = usePortal();
  const api = useApi();
  const confirm = useConfirm();
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const list = usePortalList<PersonRow>('contacts', { q: q || undefined }, { enabled: can('users.manage'), limit: 50 });
  const roles = usePortalGet<{ data: RoleRow[] }>('/contacts/roles', { enabled: can('users.manage'), staleTime: 300_000 });
  const sites = usePortalGet<{ data: { id: string; name: string }[] }>('/contacts/sites', { enabled: can('users.manage'), staleTime: 300_000 });
  const act = (id: string, name: string, body: Record<string, unknown> = {}) => api.resource('contacts').action(name, { id, body });
  const invite = usePortalMutation((v: { id: string; role_key?: string }) => act(v.id, 'invite', v.role_key ? { role_key: v.role_key } : {}), { invalidate: ['contacts'], success: 'Invitation sent' });
  const disable = usePortalMutation((id: string) => act(id, 'disable'), { invalidate: ['contacts'], success: 'Portal access disabled' });
  const enable = usePortalMutation((id: string) => act(id, 'enable'), { invalidate: ['contacts'], success: 'Portal access enabled' });
  const setRole = usePortalMutation((v: { id: string; role_key: string }) => act(v.id, 'set-role', { role_key: v.role_key }), { invalidate: ['contacts'], success: 'Role updated' });
  const create = usePortalMutation((body: Record<string, unknown>) => api.request('/v1/contacts', { method: 'POST', body }), { invalidate: ['contacts'], success: 'Colleague added', onSuccess: () => setAdding(false) });

  const roleOptions = useMemo(() => (roles.data?.data ?? []).map((r) => ({ value: r.key, label: r.name })), [roles.data]);
  if (!can('users.manage')) return <EmptyState title="Only client administrators can manage portal users" icon="lock" />;

  const addFields: FieldMeta[] = [
    f('name', 'Full name', 'text', true),
    f('email', 'Email', 'email', true),
    { ...f('site_id', 'Site', 'enum', false), enum: (sites.data?.data ?? []).map((s) => ({ value: s.id, label: s.name })) },
    f('title', 'Job title', 'text', false),
    f('phone', 'Phone', 'phone', false),
    { ...f('portal_access_level', 'Can see tickets for', 'enum', true), enum: [{ value: 'own', label: 'Only their own' }, { value: 'site', label: 'Their site' }, { value: 'client', label: 'The whole organisation' }], default: 'own' },
    { ...f('role_key', 'Portal role', 'enum', false), enum: roleOptions, help: 'Leave blank for the default role.' },
    { ...f('invite', 'Send a portal invitation now', 'boolean', false), default: true },
  ];

  return (
    <div>
      <PageHeader title="Users" subtitle={`People at ${me.client?.name ?? 'your organisation'} and their portal access`} actions={<Button variant="primary" icon="user-plus" onClick={() => setAdding(true)}>Add colleague</Button>} />
      <div className="pt-toolbar"><Input className="pt-search" placeholder="Search people…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search people" /></div>
      <ListState loading={list.isLoading} error={list.error} count={list.rows.length} empty={{ title: q ? 'Nobody matches' : 'No colleagues yet', icon: 'users' }}>
        <div>
          {list.rows.map((p) => {
            const self = p.id === me.contact.id;
            const status = p.portal_status ?? (p.portal_enabled ? 'invited' : null);
            const items = [
              ...(status === 'active' ? [{ key: 'disable', label: 'Disable portal access', icon: 'ban' as const, danger: true, onSelect: () => void confirm({ title: 'Disable portal access?', body: `${p.name} will no longer be able to sign in.`, confirmLabel: 'Disable', danger: true }).then((ok) => ok && disable.mutate(p.id)) }] : []),
              ...(status === 'disabled' ? [{ key: 'enable', label: 'Enable portal access', icon: 'check' as const, onSelect: () => enable.mutate(p.id) }] : []),
              ...(status === null || status === 'invited' ? [{ key: 'invite', label: status === 'invited' ? 'Resend invitation' : 'Invite to the portal', icon: 'mail' as const, onSelect: () => invite.mutate({ id: p.id }) }] : []),
              ...(status && roleOptions.length ? [{ key: 'h', label: 'Portal role', heading: true }, ...roleOptions.map((r) => ({ key: `role-${r.value}`, label: r.label, checked: p.portal_role_key === r.value, onSelect: () => setRole.mutate({ id: p.id, role_key: r.value }) }))] : []),
            ];
            return (
              <div key={p.id} className="pt-person">
                <Avatar name={p.name} />
                <div>
                  <div><b style={{ fontWeight: 500 }}>{p.name}</b>{self && <span className="hf-muted"> (you)</span>}{p.title && <span className="hf-muted"> · {p.title}</span>}</div>
                  <div className="sub">{p.email}{p.site_name ? ` · ${p.site_name}` : ''}{p.portal_role_name ? ` · ${p.portal_role_name}` : ''}{p.last_login_at ? ` · last seen ${formatDate(p.last_login_at)}` : ''}</div>
                </div>
                <div className="hf-row">
                  <Badge tone={status === 'active' ? 'pos' : status === 'invited' ? 'warn' : status === 'disabled' ? 'neg' : 'muted'}>{status ?? 'no access'}</Badge>
                  {!self && items.length > 0 && <DropdownMenu trigger={<button className="hf-iconbtn" type="button" aria-label={`Actions for ${p.name}`}><Icon name="more-horizontal" size={16} /></button>} items={items} />}
                </div>
              </div>
            );
          })}
        </div>
        <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onMore={list.loadMore} />
      </ListState>
      <Dialog open={adding} onClose={() => setAdding(false)} title="Add a colleague" description="They join your organisation's contacts; the invitation email lets them set up portal sign-in." locked={create.isPending}>
        <AutoForm fields={addFields} mode="create" layout="stack" submitLabel="Add" busy={create.isPending} onCancel={() => setAdding(false)} onSubmit={(v) => create.mutateAsync(v)} />
      </Dialog>
    </div>
  );
}

function f(name: string, label: string, type: FieldMeta['type'], required: boolean): FieldMeta {
  return { name, label, type, required, readOnly: false, nullable: !required, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: true, inList: false, inDetail: true };
}

// ── Profile ───────────────────────────────────────────────────────────────────
export function ProfilePage() {
  const { me, can, refresh } = usePortal();
  const api = useApi();
  const prefs = me.user.notification_prefs ?? {};
  const save = usePortalMutation((body: Record<string, unknown>) => api.request('/v1/me', { method: 'PATCH', body }), { invalidate: refresh, success: 'Profile saved' });
  const setPrefs = usePortalMutation((body: { email?: boolean; in_app?: boolean }) => api.resource('notifications').action('prefs', { body }), { invalidate: refresh, success: 'Preferences saved' });
  const fields: FieldMeta[] = [
    f('name', 'Full name', 'text', true),
    f('title', 'Job title', 'text', false),
    f('department', 'Department', 'text', false),
    f('phone', 'Phone', 'phone', false),
    f('mobile', 'Mobile', 'phone', false),
    { ...f('preferred_contact_method', 'Preferred contact method', 'enum', false), enum: [{ value: 'email', label: 'Email' }, { value: 'phone', label: 'Phone' }, { value: 'mobile', label: 'Mobile' }, { value: 'sms', label: 'SMS' }] },
  ];
  return (
    <div className="pt-main narrow" style={{ padding: 0 }}>
      <PageHeader title="Profile" subtitle={me.user.email} />
      <div className="hf-stack" style={{ gap: 'var(--s-5)' }}>
        <dl className="hf-kv">
          <div><dt>Organisation</dt><dd>{me.client?.name ?? '—'}{me.site ? ` · ${me.site.name}` : ''}</dd></div>
          <div><dt>Portal role</dt><dd>{me.role.name ?? 'Default'}</dd></div>
          <div><dt>Can see tickets for</dt><dd>{{ own: 'Only your own', site: 'Your site', client: 'Your whole organisation', none: 'Nothing' }[me.access_level]}</dd></div>
        </dl>
        <section>
          <h2 className="hf-section-title">Your details</h2>
          {can('profile.edit') ? (
            <AutoForm fields={fields} mode="edit" layout="stack" initial={me.contact} submitLabel="Save" busy={save.isPending} onSubmit={(v) => save.mutateAsync(v)} autoFocus={false} />
          ) : (
            <Banner tone="info">Your portal role cannot edit these details. Ask your administrator.</Banner>
          )}
        </section>
        <section>
          <h2 className="hf-section-title">Notifications</h2>
          <div className="hf-stack">
            <Switch label="Email me when a ticket is updated or a decision is needed" checked={prefs.email !== false} onChange={(e) => setPrefs.mutate({ email: e.target.checked })} />
            <Switch label="Show notifications in the portal" checked={prefs.in_app !== false} onChange={(e) => setPrefs.mutate({ in_app: e.target.checked })} />
          </div>
        </section>
      </div>
    </div>
  );
}

// ── Notifications ─────────────────────────────────────────────────────────────
export function NotificationsPage() {
  const api = useApi();
  const list = usePortalList<NotificationRow>('notifications', {}, { limit: 30 });
  const readAll = usePortalMutation((_: void) => api.resource('notifications').action('read-all'), { invalidate: ['notifications'], success: 'All caught up' });
  const read = usePortalMutation((id: string) => api.resource('notifications').action('read', { id }), { invalidate: ['notifications'] });
  const unread = list.rows.filter((n) => !n.read_at).length;
  const linkOf = (n: NotificationRow) => {
    if (n.entity_type === 'ticket' && n.entity_id) return `/portal/tickets/${n.entity_id}`;
    if (n.entity_type === 'approval_run' && n.entity_id) return `/portal/approvals/${n.entity_id}`;
    if (n.link?.startsWith('/portal')) return n.link;
    return null;
  };
  return (
    <div className="pt-main narrow" style={{ padding: 0 }}>
      <PageHeader title="Notifications" subtitle={unread > 0 ? `${unread} unread` : 'You are up to date'} actions={unread > 0 ? <Button variant="outline" size="sm" busy={readAll.isPending} onClick={() => readAll.mutate()}>Mark all read</Button> : null} />
      <ListState loading={list.isLoading} error={list.error} count={list.rows.length} empty={{ title: 'No notifications', icon: 'bell' }}>
        <div className="pt-cards">
          {list.rows.map((n) => {
            const to = linkOf(n);
            const body = (
              <>
                <span className="title" style={{ fontWeight: n.read_at ? 400 : 500 }}>{n.title}</span>
                {n.body && <span className="meta"><span>{n.body}</span></span>}
                <span className="meta"><span>{formatDateTime(n.created_at)}</span></span>
                <span className="side">{!n.read_at && <Badge tone="accent" dot={false}>new</Badge>}</span>
              </>
            );
            return to ? (
              <Link key={n.id} to={to} className="pt-card" onClick={() => { if (!n.read_at) read.mutate(n.id); }}>{body}</Link>
            ) : (
              <div key={n.id} className="pt-card" onClick={() => { if (!n.read_at) read.mutate(n.id); }}>{body}</div>
            );
          })}
        </div>
        <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onMore={list.loadMore} />
      </ListState>
    </div>
  );
}
